import { useQueryClient } from '@tanstack/react-query';
import { type NotificationItem } from '@utazzo/shared';
import { Bell, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  notificationKind,
  notificationOpenLabel,
  notificationTargetPath,
  openNotificationTarget,
} from '../notification-navigation';
import { playNotificationChime } from '../notification-sound';
import * as notifApi from '../notifications.api';
import { useNotificationStream } from '../use-notification-stream';
import { useMyNotificationsInbox } from '../use-notifications';

import { useCurrentUser, useIsAuthenticated } from '@/features/auth/use-auth';

/**
 * Top-right sliding notification popup host. Mounted once next to
 * `InboxBell` in `AppShell`. Subscribes to the same SSE stream the
 * bell uses; instead of just updating the badge, it slides in a card
 * from the right for the newest notification.
 *
 * Each popup carries:
 *   - The notification subject + body (or eventCode fallback).
 *   - An "Open" link when the notification has a `leadId` tag.
 *   - A snooze dropdown (10 / 20 / 30 minutes) that dismisses the
 *     card and re-shows it after the chosen interval.
 *   - A close button.
 *
 * 2026-07-24. Only OPENING a notification retires it. Closing it does not.
 *
 * There are two ways off the screen and they mean different things:
 *   - "Open lead" (or a click on the card body) - the operator is going to
 *     deal with it. Marked read on the server; never shown again.
 *   - X, and snooze - "not now". The card goes away, the row stays UNREAD,
 *     and it comes back: after the chosen interval for snooze, after
 *     `REPOP_COOLDOWN_MS` for X. It keeps coming back until it is opened.
 *
 * The exception is a notification with nothing to open (no `leadId` /
 * `customerId` tag, so no "Open lead" button). There, X IS the only action
 * available, so it retires the notification the way opening would - otherwise
 * the card would nag forever with no way to answer it.
 *
 * The re-show survives a reload: the cooldown is persisted per occurrence
 * (see `POPPED_STORAGE_PREFIX`), so it does not depend on a `setTimeout`
 * living long enough to fire.
 *
 * Redis fallback: this component depends only on the stream hook,
 * which itself is no-op when Redis is down (the bell's polling
 * fallback keeps the inbox query fresh). When operators want the
 * popup on a polling-only path, the polling-refresh below also
 * triggers a popup for any NEW unread row since the last poll.
 */
/**
 * 2026-07-23. ONE card on screen at a time - always the newest.
 *
 * Was 4. A stack of cards covered the top-right corner of whatever the
 * operator was actually working on, and on a busy queue it read as spam
 * rather than as an alert. The bell + inbox are the place to catch up on
 * everything; the popup's job is only "something just happened".
 *
 * `pushPopup` prepends and slices to this cap, so a newer notification
 * REPLACES the one on screen. The replaced card is not marked read - it
 * stays unread in the bell, waiting, exactly like every other one the
 * operator hasn't opened yet. Nothing is lost, it just isn't shouted at
 * them twice over.
 */
const MAX_VISIBLE_POPUPS = 1;

/**
 * 2026-07-24. When each occurrence last had a card, persisted per user.
 *
 * This used to be a Set of "already popped, never again" keys. That made
 * closing a card the same as dealing with it: one X and the alert was gone
 * for good, even though the work behind it had not been touched. An operator
 * who cleared a card to see what was underneath it never heard about that
 * lead again.
 *
 * It is now a cooldown, not a tombstone. An occurrence that is still UNREAD
 * becomes eligible for another card once `REPOP_COOLDOWN_MS` has passed, and
 * keeps coming back until the operator actually opens it (which marks it
 * read - see `dismissPermanently`). Storing the timestamp rather than the
 * bare key is what lets that survive a reload, where every in-memory timer is
 * already gone.
 *
 * The original reason for persisting is untouched: a login with a big unread
 * backlog still gets ONE card, because every eligible row is stamped on the
 * same pass while only the newest is shown.
 *
 * Capped so the entry cannot grow without bound on a long-lived login; the
 * oldest keys fall off first and the notifications they refer to are long
 * read by then.
 */
const POPPED_STORAGE_PREFIX = 'utazzo.notifications.popped.';
const POPPED_HISTORY_LIMIT = 300;

/**
 * 2026-07-24. How long a closed-but-unread card stays quiet before it comes
 * back.
 *
 * Deliberately longer than `CONTENT_DEDUPE_WINDOW_MS` so the two never race:
 * by the time a card is due to reappear, the content-dedupe stamp that a
 * permanent dismiss leaves behind has already expired.
 *
 * 10 minutes matches the shortest explicit snooze, which is the honest
 * reading of pressing X - "not now", not "never".
 */
const REPOP_COOLDOWN_MS = 10 * 60 * 1000;
const SNOOZE_OPTIONS = [
  { value: 10, label: 'Snooze 10 min' },
  { value: 20, label: 'Snooze 20 min' },
  { value: 30, label: 'Snooze 30 min' },
] as const;

interface ActivePopup {
  /// Unique popup instance id - distinct from the notification id so
  /// the same notification can re-appear after a snooze.
  key: string;
  notification: NotificationItem;
}

/// 2026-06-25: collapse content-duplicate notifications (same
/// event + lead + subject) so the operator doesn't see the same
/// "Default Admin assigned lead X" card three times when the
/// server emits multiple identical rows. The key is built per
/// notification; matches are skipped at push time. Hoisted to
/// module scope 2026-07-10 so the pushPopup / dismiss useCallbacks
/// don't take a fresh reference every render.
const contentKeyOf = (n: NotificationItem): string =>
  `${n.eventCode}::${n.tags?.leadId ?? ''}::${n.subject ?? n.bodyText ?? ''}`;

/**
 * 2026-07-23. The unit of "already popped" is the OCCURRENCE, not the row.
 *
 * This is what makes the sales-agent Nudge visible. The server coalesces a
 * repeat in-app notification onto an existing UNREAD row rather than writing
 * a new one (`tryCoalesceInApp`): it bumps `occurrenceCount`, refreshes
 * `createdAt`, and keeps the SAME id. A nudge on a request the reviewer has
 * not yet dealt with therefore arrives as the same id it already saw.
 *
 * Suppressing by id alone - which is what persisting popped ids did - meant
 * the nudge lit up the bell but never produced a card. The reviewer got no
 * new alert, which is precisely the thing Nudge exists to do.
 *
 * Keying on `id + occurrenceCount` gives each genuine occurrence its own one
 * popup: first request pops, nudge pops again, a second nudge pops again, and
 * a mere re-poll of the same occurrence stays silent.
 */
const occurrenceKeyOf = (n: NotificationItem): string => `${n.id}#${n.occurrenceCount ?? 1}`;

/**
 * 2026-07-24. Does this notification have somewhere to go?
 *
 * Mirrors `openNotificationTarget` - the card only renders an "Open lead"
 * button when one of these tags is present. When there is nothing to open,
 * closing the card is the operator's only possible response to it, so X has
 * to count as dealing with it. Keep the two in step: a tag added there and
 * not here would leave a card that can never be retired.
 */
const hasOpenTarget = (n: NotificationItem): boolean => notificationTargetPath(n.tags) !== null;

/**
 * 2026-07-23. How long a dismissed CONTENT key keeps blocking.
 *
 * This guard was permanent, which broke Nudge from the other direction: if
 * the reviewer dismissed the card (so the row got marked read, so the server
 * wrote the nudge as a brand-new row), the new row still carried identical
 * content and was blocked for the rest of the session.
 *
 * It only ever needed to absorb duplicate STORMS - the same row arriving
 * twice within seconds over SSE and polling together. Five minutes covers
 * that comfortably, while the nudge cooldown (`NUDGE_COOLDOWN_MS`, 1 hour)
 * guarantees a real nudge always lands well outside the window.
 */
const CONTENT_DEDUPE_WINDOW_MS = 5 * 60 * 1000;

export const NotificationPopupHost = (): React.JSX.Element | null => {
  const isAuthed = useIsAuthenticated();
  const qc = useQueryClient();
  const [popups, setPopups] = useState<ActivePopup[]>([]);
  /// Track snooze timers so unmount/logout clears them.
  const snoozeTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  /// When each occurrence key (`id#occurrenceCount`) last had a card,
  /// persisted per user - see `occurrenceKeyOf` and `POPPED_STORAGE_PREFIX`.
  /// A re-poll within `REPOP_COOLDOWN_MS` is silent; after it, an unread
  /// occurrence is due another card. A nudge bumps the count and is a new key,
  /// so it never waits on the cooldown.
  const shownAtRef = useRef<Map<string, number>>(new Map());
  /// Occurrences that have been RETIRED - opened, or closed when there was
  /// nothing to open. These must never re-popup; the bell + inbox still
  /// surface them. Distinct from `shownAtRef`, which only defers.
  const dismissedKeysRef = useRef<Set<string>>(new Set());
  /// 2026-07-10. Also track dismissed CONTENT keys so a duplicate
  /// notification (same eventCode + leadId + subject but a fresh
  /// server-side id) can't slip past `dismissedKeysRef`. Fixes the
  /// "X keeps popping back" symptom when the server re-emits an
  /// identical row (retries / SSE + polling both delivering, etc.).
  /// 2026-07-23: now a key -> timestamp map, expiring after
  /// `CONTENT_DEDUPE_WINDOW_MS`, so it stops swallowing nudges.
  const dismissedContentAtRef = useRef<Map<string, number>>(new Map());
  /// 2026-07-19. Ids the operator has marked READ (from the bell, or via
  /// "mark all read"). Previously mark-as-read was invisible to this
  /// component: the only suppression sets were the two above, populated
  /// solely by the X button / "Open lead". So a row could be read in the
  /// bell and still slide in as a card - and a snooze timer would happily
  /// re-push a card the operator had already dealt with.
  ///
  /// This matters more than it looks. The server coalesces repeat in-app
  /// notifications onto one row ONLY while that row is unread
  /// (notification-dispatcher.service.ts, `readAt: null` in the lookup).
  /// Marking read therefore ends the coalescing window, and every later
  /// occurrence becomes its own row with its own id - which sails past
  /// `shownAtRef` and pops individually. Filtering on read state here is
  /// the client-side half of containing that.
  const readIdsRef = useRef<Set<string>>(new Set());
  /// Which storage key `shownAtRef` was hydrated from, so a re-login as a
  /// different user re-reads that user's own set instead of inheriting the
  /// previous one's.
  const hydratedForKeyRef = useRef<string | null>(null);
  const userId = useCurrentUser()?.id ?? null;
  const poppedStorageKey = userId ? `${POPPED_STORAGE_PREFIX}${userId}` : null;

  /// Hydrate `shownAtRef` from localStorage. Cheap, idempotent, and called
  /// at the top of the inbox effect rather than in its own effect so it is
  /// guaranteed to have run before the first eligibility check below.
  ///
  /// Reads both shapes: `[[key, ts], ...]` (current) and `[key, ...]` (the
  /// pre-2026-07-24 tombstone list). A legacy key hydrates at timestamp 0, so
  /// anything left unread under the old "never again" rule is immediately due
  /// a card again - which is the behaviour this release is restoring.
  const hydratePopped = useCallback((): void => {
    if (!poppedStorageKey || hydratedForKeyRef.current === poppedStorageKey) return;
    hydratedForKeyRef.current = poppedStorageKey;
    try {
      const raw = window.localStorage.getItem(poppedStorageKey);
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) return;
      for (const entry of parsed) {
        if (typeof entry === 'string') {
          shownAtRef.current.set(entry, 0);
        } else if (Array.isArray(entry) && typeof entry[0] === 'string') {
          shownAtRef.current.set(entry[0], typeof entry[1] === 'number' ? entry[1] : 0);
        }
      }
    } catch {
      // Corrupt entry or storage unavailable (private mode). Worst case the
      // operator sees one repeat card - not worth failing the component over.
    }
  }, [poppedStorageKey]);

  /// Stamp an occurrence as shown NOW, starting its cooldown, and persist it.
  /// Re-stamping an existing key moves it to the tail of the map, so the
  /// trim below drops genuinely stale keys rather than actively-nagging ones.
  const rememberPopped = useCallback(
    (occurrenceKey: string): void => {
      shownAtRef.current.delete(occurrenceKey);
      shownAtRef.current.set(occurrenceKey, Date.now());
      if (!poppedStorageKey) return;
      try {
        // Maps iterate in insertion order, so the tail is the most recent.
        const trimmed = [...shownAtRef.current.entries()].slice(-POPPED_HISTORY_LIMIT);
        window.localStorage.setItem(poppedStorageKey, JSON.stringify(trimmed));
      } catch {
        // Quota / private mode. Same reasoning as above.
      }
    },
    [poppedStorageKey],
  );

  /// Is this occurrence due a card? Never shown, or shown longer ago than the
  /// cooldown. Read state is checked separately by the caller.
  const isDueAgain = useCallback((n: NotificationItem): boolean => {
    const shownAt = shownAtRef.current.get(occurrenceKeyOf(n));
    return shownAt === undefined || Date.now() - shownAt >= REPOP_COOLDOWN_MS;
  }, []);

  /// 2026-07-23. Persist the dismissal to the SERVER, not just to the
  /// in-memory sets above.
  ///
  /// Every suppression set in this component dies with the page. Before
  /// this, closing a card (X) or opening its lead left the row `readAt:
  /// null` forever, so the next reload - or the next login - found it
  /// unread and slid it in again. That is the "I keep getting the same
  /// notifications even after closing them" report: the operator was
  /// dismissing a card, not a notification.
  ///
  /// Marking read is also what ENDS the server's coalescing window
  /// (notification-dispatcher.service.ts looks up `readAt: null`), so
  /// leaving rows unread forever meant repeat occurrences kept merging
  /// onto one ever-growing row instead of behaving predictably.
  const markReadOnServer = useCallback(
    (notificationId: string): void => {
      // Idempotent: `readIdsRef` is also the guard `pushPopup` reads, so
      // setting it first blocks any in-flight snooze re-push immediately,
      // without waiting for the round trip.
      if (readIdsRef.current.has(notificationId)) return;
      readIdsRef.current.add(notificationId);
      void notifApi
        .markNotificationRead(notificationId)
        .then(() => {
          // Refresh the bell badge / inbox list so the count drops in the
          // same beat the card disappears.
          void qc.invalidateQueries({ queryKey: ['notifications', 'me', 'inbox'] });
        })
        .catch(() => {
          // Network blip. Deliberately silent - no toast for a background
          // bookkeeping call the operator never asked for. The card is
          // already gone for this session, and the backlog prime below
          // stops it re-popping next load, so the worst case is a stale
          // unread badge, not a repeat popup.
        });
    },
    [qc],
  );

  const pushPopup = useCallback((notification: NotificationItem): void => {
    // Hard block 0: already read - nothing to alert the operator about.
    // Covers the snooze re-push path too, which captures a stale
    // `notification` object and would otherwise ignore later read state.
    if (readIdsRef.current.has(notification.id) || notification.readAt !== null) return;
    // Hard block 1: operator explicitly closed THIS occurrence earlier. A
    // nudge bumps `occurrenceCount`, so it is a different key and gets
    // through - which is the whole point of nudging.
    if (dismissedKeysRef.current.has(occurrenceKeyOf(notification))) return;
    // Hard block 1b: operator closed a DIFFERENT row with the SAME content
    // moments ago (a duplicate storm). Time-boxed - see
    // `CONTENT_DEDUPE_WINDOW_MS` - so a nudge an hour later still lands.
    const contentKey = contentKeyOf(notification);
    const dismissedAt = dismissedContentAtRef.current.get(contentKey);
    if (dismissedAt !== undefined && Date.now() - dismissedAt < CONTENT_DEDUPE_WINDOW_MS) return;
    setPopups((prev) => {
      const key = `${notification.id}:${Date.now()}`;
      // Hard block 2: same id is already on screen (snooze re-show
      // races with stream / polling re-fetch).
      if (prev.some((p) => p.notification.id === notification.id)) return prev;
      // Hard block 3: content-duplicate (server emitted multiple
      // identical rows for the same event + lead). Show only the
      // first; the others remain in the inbox bell.
      if (prev.some((p) => contentKeyOf(p.notification) === contentKey)) return prev;
      const next = [{ key, notification }, ...prev];
      // Cap visible count - older popups drop off (operator can still
      // see them in the bell inbox).
      return next.slice(0, MAX_VISIBLE_POPUPS);
    });
  }, []);

  /// Take the card off screen without deciding anything about the
  /// notification behind it. Both exits below start here.
  const hidePopup = useCallback((popup: ActivePopup): void => {
    setPopups((prev) => prev.filter((p) => p.key !== popup.key));
  }, []);

  /// The operator has DEALT with it - opened the lead, or closed a card that
  /// had nothing to open. Retire it: never re-push, and mark read so the
  /// decision survives the reload (and so the server stops coalescing later
  /// occurrences onto this row).
  const dismissPermanently = useCallback(
    (popup: ActivePopup): void => {
      const { notification } = popup;
      hidePopup(popup);
      // Block this exact occurrence from any later re-push (e.g. a pending
      // remind-later timer that captured a stale object).
      dismissedKeysRef.current.add(occurrenceKeyOf(notification));
      // And stamp the content key so a duplicate row arriving in the next
      // few minutes with a fresh id is swallowed too. Time-boxed, so a nudge
      // (1h cooldown) is never caught by it.
      dismissedContentAtRef.current.set(contentKeyOf(notification), Date.now());
      markReadOnServer(notification.id);
    },
    [hidePopup, markReadOnServer],
  );

  /// "Not now." Used by X and by snooze. The row stays UNREAD - the operator
  /// has not answered it, and it is supposed to come back and say so again.
  ///
  /// Two things bring it back, deliberately: the timer here (precise, dies
  /// with the page) and the cooldown stamp (survives a reload, resolves on
  /// the next inbox refresh). Whichever lands first wins; the second is a
  /// no-op because `pushPopup` refuses an id already on screen.
  const remindLater = useCallback(
    (popup: ActivePopup, delayMs: number): void => {
      hidePopup(popup);
      // Restart the cooldown from the dismissal, not from when the card first
      // appeared - otherwise a card closed near the end of its cooldown would
      // be re-pushed by the inbox effect within seconds.
      rememberPopped(occurrenceKeyOf(popup.notification));
      const timer = setTimeout(() => {
        snoozeTimersRef.current.delete(timer);
        pushPopup(popup.notification);
      }, delayMs);
      snoozeTimersRef.current.add(timer);
    },
    [hidePopup, pushPopup, rememberPopped],
  );

  // Cleanup any in-flight snoozes when the user logs out.
  useEffect(() => {
    if (isAuthed) return;
    for (const t of snoozeTimersRef.current) clearTimeout(t);
    snoozeTimersRef.current.clear();
    shownAtRef.current.clear();
    dismissedKeysRef.current.clear();
    dismissedContentAtRef.current.clear();
    readIdsRef.current.clear();
    // Force a re-hydrate on the next login so it reads THAT user's popped
    // set. The localStorage entry itself is deliberately left alone - it is
    // the whole mechanism for "shown once, ever", and it is per-user keyed.
    hydratedForKeyRef.current = null;
    setPopups([]);
  }, [isAuthed]);

  /// Mirror read state from the bell's inbox cache. Reads the SAME query key
  /// the bell subscribes to, so React Query serves it from cache - this adds
  /// no extra request. Two jobs: remember read ids so `pushPopup` can refuse
  /// them, and pull any on-screen card for a notification that has since been
  /// read (clicking "mark read" in the bell should clear the card, which it
  /// previously did not).
  /// `refetchInterval` MUST match InboxBell's 60s. Same query key means one
  /// shared cache entry, and React Query polls at the SHORTEST interval any
  /// observer asks for - passing the hook's 30s default would silently double
  /// the inbox request rate for every logged-in operator.
  const inbox = useMyNotificationsInbox({ limit: 20 }, { refetchInterval: 60_000 });
  const inboxRows = inbox.data?.data;
  useEffect(() => {
    if (!inboxRows) return;
    hydratePopped();

    // (a) Mirror read state: a row read from the bell should clear its card.
    const newlyRead: string[] = [];
    for (const n of inboxRows) {
      if (n.readAt === null) continue;
      if (readIdsRef.current.has(n.id)) continue;
      readIdsRef.current.add(n.id);
      newlyRead.push(n.id);
    }
    if (newlyRead.length > 0) {
      setPopups((prev) => prev.filter((p) => !newlyRead.includes(p.notification.id)));
    }

    // (b) Pop the newest notification that has never had its popup.
    //
    // 2026-07-23. This replaced a 60s `setInterval` that scanned the same
    // cache. Driving off the query data instead means the card appears the
    // moment the inbox updates - whether that came from SSE, the bell's
    // poll, or a window refocus - rather than up to a minute later. It also
    // deletes a timer that duplicated work React Query already does.
    //
    // Server returns the inbox `createdAt: 'desc'`, so index 0 is newest -
    // and a coalesced row (a nudge) is bumped back to the top by the same
    // `createdAt: new Date()` write, so a nudge naturally wins this race.
    // EVERY eligible row is marked popped, but only the newest is shown:
    // that is what keeps a login with a big unread backlog to one card
    // instead of a wall, without dripping the rest out one per refresh.
    //
    // 2026-07-24. "Due" now means never shown OR past its cooldown, so an
    // unread row the operator closed earlier comes back here as well as via
    // its timer - which is what makes the re-show survive a reload.
    const unseen = inboxRows.filter((n) => n.readAt === null && isDueAgain(n));
    const newest = unseen[0];
    if (!newest) return;
    for (const n of unseen) rememberPopped(occurrenceKeyOf(n));
    // Informational booking rows (a confirmed booking, a guest's own cancel)
    // get the card but not the chime - see the same rule on the bell.
    if (notificationKind(newest.tags) === 'action') void playNotificationChime();
    pushPopup(newest);
  }, [inboxRows, hydratePopped, rememberPopped, pushPopup, isDueAgain]);

  /// Stream handler: pull the fresh inbox and write it into the bell's cache.
  ///
  /// 2026-07-23. It no longer pushes the popup itself. The SSE payload is
  /// only `{ id, ts }` - it carries no `occurrenceCount`, so this path cannot
  /// tell a first delivery from a nudge without the row anyway. Writing the
  /// data and letting the inbox effect above decide gives ONE eligibility
  /// rule for both transports (SSE and the bell's poll), instead of two that
  /// have to be kept in agreement - which is how the SSE path drifted into
  /// pushing already-read rows back in July.
  const handleStream = useCallback(
    async (_payload: { id: string; ts: string }) => {
      try {
        // 2026-07-23. Fetch 20, not 5. The response is written straight into
        // the bell's `{ limit: 20 }` cache entry below, so fetching 5 was
        // truncating the operator's inbox list to 5 rows every time a
        // notification arrived - the bell visibly shrank. Same limit in and
        // out keeps the cache entry honest to its key.
        const inbox = await notifApi.getMyNotificationsInbox({ limit: 20 });
        qc.setQueryData(['notifications', 'me', 'inbox', { limit: 20 }], inbox);
      } catch {
        // Network blip - the bell's polling fallback will pick this up.
      }
    },
    [qc],
  );

  useNotificationStream({ enabled: isAuthed, onNotification: handleStream });

  // 2026-07-23. The Redis-offline fallback used to be a second 60s
  // `setInterval` scanning this same cache entry. It is gone - the inbox
  // effect above now reacts to the query data directly, which covers the
  // no-SSE path (the bell's poll refreshes the cache, the effect fires) and
  // the SSE path alike, without a timer and without up to 60s of lag.

  if (!isAuthed || popups.length === 0) return null;

  return (
    <div
      role="region"
      aria-label="Notification popups"
      className="pointer-events-none fixed top-16 right-4 z-50 flex w-80 flex-col gap-2"
    >
      {popups.map((p) => (
        <NotificationPopupCard
          key={p.key}
          notification={p.notification}
          onClose={() => {
            // X means "not now" - unless there is nothing to open, in which
            // case it is the only answer the operator can give. See the
            // component doc comment.
            if (hasOpenTarget(p.notification)) remindLater(p, REPOP_COOLDOWN_MS);
            else dismissPermanently(p);
          }}
          onSnooze={(min) => remindLater(p, min * 60 * 1000)}
          onOpen={() => {
            dismissPermanently(p);
            openNotificationTarget(p.notification.tags);
          }}
        />
      ))}
    </div>
  );
};

interface NotificationPopupCardProps {
  notification: NotificationItem;
  onClose: () => void;
  onSnooze: (minutes: number) => void;
  onOpen: () => void;
}

const NotificationPopupCard = ({
  notification,
  onClose,
  onSnooze,
  onOpen,
}: NotificationPopupCardProps): React.JSX.Element => {
  const target = notificationTargetPath(notification.tags);
  const openable = target !== null;
  // A booking that needs a decision wears amber; everything else keeps the
  // blue it always had. Same signal as the bell row and the bookings list.
  const urgent =
    Boolean(notification.tags?.bookingId) && notificationKind(notification.tags) === 'action';
  const title = notification.subject ?? notification.eventCode;
  const body = notification.bodyText ?? notification.lead?.title ?? null;
  return (
    <div
      role="alert"
      className={`animate-in slide-in-from-right-4 fade-in-0 pointer-events-auto flex flex-col gap-2 rounded-md border bg-white p-3 shadow-lg duration-300 ${
        urgent ? 'border-amber-300' : 'border-zinc-200'
      } ${openable ? 'cursor-pointer hover:border-blue-200' : ''}`}
      onClick={openable ? onOpen : undefined}
    >
      <div className="flex items-start gap-2">
        <span
          className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
            urgent ? 'bg-amber-100 text-amber-700' : 'bg-blue-50 text-blue-600'
          }`}
        >
          <Bell className="h-3.5 w-3.5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-zinc-900">{title}</div>
          {body && <div className="mt-0.5 line-clamp-2 text-xs text-zinc-600">{body}</div>}
          {notification.lead && (
            <div className="mt-1 truncate text-[11px] text-zinc-500">
              {notification.lead.reference} - {notification.lead.title}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Dismiss notification"
          className="rounded p-0.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex items-center gap-2">
        {openable && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
            className={`rounded px-2 py-1 text-xs font-medium text-white ${
              urgent ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {notificationOpenLabel(notification.tags)}
          </button>
        )}
        <select
          aria-label="Snooze duration"
          defaultValue=""
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            e.stopPropagation();
            const v = Number(e.target.value);
            if (Number.isFinite(v) && v > 0) onSnooze(v);
          }}
          className="ml-auto rounded border border-zinc-200 bg-white px-2 py-1 text-xs text-zinc-700"
        >
          <option value="" disabled>
            Snooze...
          </option>
          {SNOOZE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};
